import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { saveCachedChartData } from './dashboardFetchCache';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import client from 'client';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

// Generamos el array de fechas de la semana y su formato
const week = [
  moment().day('Sunday').format('MM/DD/YYYY'),
  moment().day('Monday').format('MM/DD/YYYY'),
  moment().day('Tuesday').format('MM/DD/YYYY'),
  moment().day('Wednesday').format('MM/DD/YYYY'),
  moment().day('Thursday').format('MM/DD/YYYY'),
  moment().day('Friday').format('MM/DD/YYYY'),
  moment().day('Saturday').format('MM/DD/YYYY'),
];

const formatDateArray = (dateArray) =>
  dateArray.map((dateStr) => moment(dateStr, 'MM/DD/YYYY').format('ddd MM/DD'));

const formattedArray = formatDateArray(week);

// Clave única para la semana actual (para cache)
const weekKey = `${moment(week[0]).format('YYYYMMDD')}_${moment(week[6]).format('YYYYMMDD')}`;

const formatNumber = (num) => {
  let formatted = num.toLocaleString();
  // Asegura que tenga dos decimales en caso de tener decimales
  if (formatted.includes('.') && formatted.split('.')[1].length < 2) {
    formatted += '0';
  }
  // Elimina separadores de miles si existen
  return formatted.replace(/,/g, '');
};

function resolveTopDriverPeriod(driver) {
  const raw = driver?.current_period;
  return Array.isArray(raw) ? raw[0] : raw;
}

function pickStr(...vals) {
  for (const v of vals) {
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

function splitFullName(full) {
  if (!full || typeof full !== 'string') return { first: '', last: '' };
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/**
 * Top 3 Drivers: `dispatch.topDriversData[]` — `current_period.total_revenue`, `growth.revenue`,
 * nombres desde periodo o raíz / `full_name` / `name`.
 */
function normalizeTopDriverRow(driver) {
  if (!driver || typeof driver !== 'object') return driver;
  const period = resolveTopDriverPeriod(driver);
  const periodRevenue = period?.total_revenue;
  const n =
    periodRevenue != null && periodRevenue !== ''
      ? parseFloat(periodRevenue)
      : parseFloat(driver.total_revenue);

  let user_fname = pickStr(
    period?.user_fname,
    period?.first_name,
    period?.fname,
    driver.user_fname,
    driver.first_name,
    driver.fname
  );
  let user_lname = pickStr(
    period?.user_lname,
    period?.last_name,
    period?.lname,
    driver.user_lname,
    driver.last_name,
    driver.lname
  );

  if (!user_fname && !user_lname) {
    const full = pickStr(
      period?.full_name,
      period?.name,
      period?.driver_name,
      driver.full_name,
      driver.name,
      driver.driver_name
    );
    if (full) {
      const sp = splitFullName(full);
      user_fname = sp.first;
      user_lname = sp.last;
    }
  }

  const driverImageLocation = pickStr(
    period?.driverImageLocation,
    period?.avatar,
    driver.driverImageLocation,
    driver.avatar
  );

  const rawGrowth = driver.growth;
  const growthObj = Array.isArray(rawGrowth) ? rawGrowth[0] : rawGrowth;
  const growthRevRaw = growthObj?.revenue;
  const growthRevenueParsed = parseFloat(growthRevRaw);

  return {
    ...driver,
    total_revenue: Number.isFinite(n) ? n : 0,
    user_fname: user_fname || undefined,
    user_lname: user_lname || undefined,
    growthRevenue: Number.isFinite(growthRevenueParsed) ? growthRevenueParsed : null,
    ...(driverImageLocation ? { driverImageLocation } : {}),
  };
}

function resolveTopCustomerPeriod(customer) {
  const raw = customer?.current_period;
  return Array.isArray(raw) ? raw[0] : raw;
}

/**
 * Top Customers: lista en `topCustomers.customers`; importe desde `current_period.total_revenue`.
 */
function normalizeTopCustomerRow(customer) {
  if (!customer || typeof customer !== 'object') return customer;
  const period = resolveTopCustomerPeriod(customer);
  const periodRevenue = period?.total_revenue;
  const n =
    periodRevenue != null && periodRevenue !== ''
      ? parseFloat(periodRevenue)
      : parseFloat(customer.total_revenue ?? customer.revenue ?? NaN);

  const customer_name = pickStr(
    period?.customer_name,
    period?.name,
    customer.customer_name,
    customer.name
  );

  const rawGrowth = customer.growth;
  const growthObj = Array.isArray(rawGrowth) ? rawGrowth[0] : rawGrowth;
  const growthRevRaw = growthObj?.revenue;
  const growthRevenueParsed = parseFloat(growthRevRaw);

  return {
    ...customer,
    total_revenue: Number.isFinite(n) ? n : 0,
    growthRevenue: Number.isFinite(growthRevenueParsed) ? growthRevenueParsed : null,
    ...(customer_name ? { customer_name } : {}),
  };
}

export function extractTopDriversList(summaryData) {
  const dispatch = summaryData.dispatch || {};
  return (dispatch.topDriversData || []).slice(0, 3).map(normalizeTopDriverRow);
}

export function extractTopCustomersList(summaryData) {
  const customersList =
    summaryData.topCustomers?.customers ??
    (Array.isArray(summaryData.topCustomers) ? summaryData.topCustomers : []);
  return (Array.isArray(customersList) ? customersList : []).map(normalizeTopCustomerRow);
}

function extractDispatchDataFromSummary(summaryData) {
  const dispatch = summaryData?.dispatch || {};
  const trucksData = dispatch.trucksData?.[0] || {};
  const driversData = dispatch.driversData?.[0] || {};
  return {
    trucksInUse: parseInt(trucksData.trucks_in_use, 10) || 0,
    trucksAvailable: parseInt(trucksData.trucks_available, 10) || 0,
    driversAvailable: parseInt(driversData.drivers_available, 10) || 0,
    totalTrucks: parseInt(trucksData.total_trucks, 10) || 0,
  };
}

/**
 * Gráfico + finanzas + contadores de flota (columna 1). Devuelve `null` si no hay serie de chart válida.
 */
function buildChartFinancePatchFromSummary(summaryData, periodKey) {
  const chartData = summaryData.chart;
  if (!chartData || chartData?.msg || !Array.isArray(chartData) || chartData.length === 0) {
    return null;
  }

  const categories = chartData.map((item) => item.label);
  const firstDate = moment(chartData[0].key).format('MM/DD/YYYY');
  const lastDate = moment(chartData[chartData.length - 1].key).format('MM/DD/YYYY');
  const dateRange = `${firstDate} to ${lastDate}`;

  const materialCr = [];
  const ticketCr = [];
  const waitTimeCr = [];
  chartData.forEach((element) => {
    materialCr.push(element.materialCr || 0);
    ticketCr.push(element.ticketCr || 0);
    waitTimeCr.push(element.waitTime || 0);
  });

  const totalHaulingSales = ticketCr.reduce((sum, val) => sum + val, 0);
  const totalMaterialSales = materialCr.reduce((sum, val) => sum + val, 0);
  const totalWaitTime = waitTimeCr.reduce((sum, val) => sum + val, 0);
  const calculatedTotalSales = totalHaulingSales + totalMaterialSales + totalWaitTime;

  const fuelCosts = (summaryData.fuel?.totals || []).map((elem) => formatNumber(elem.total));
  const totalGasCostLegacy = fuelCosts.reduce((sum, val) => sum + parseFloat(val || 0), 0);

  const ticketCosts = (summaryData.ticketCost?.period_totals || []).map((elem) => parseFloat(elem.total) || 0);
  const totalEmployeeCommissions = ticketCosts.reduce((sum, val) => sum + val, 0);

  const subconticketCosts = (summaryData.subcon?.totals || []).map((elem) => parseFloat(elem.total) || 0);
  const totalOwnerCommissions = subconticketCosts.reduce((sum, val) => sum + val, 0);

  const totalCommissionsLegacy = totalEmployeeCommissions + totalOwnerCommissions;
  const grossProfitLegacy = calculatedTotalSales - totalGasCostLegacy - totalCommissionsLegacy;

  const fmPeriod = summaryData.financialMetrics?.current_period;
  const fmGrowth = summaryData.financialMetrics?.growth;
  const parseFinancialMetric = (value, fallback) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  };
  const parseGrowthMetric = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : null;
  };

  const calculatedFinancialData = {
    totalEarnings: parseFinancialMetric(fmPeriod?.total_earnings, calculatedTotalSales),
    materialSales: parseFinancialMetric(fmPeriod?.material_sales, totalMaterialSales),
    gasCost: parseFinancialMetric(fmPeriod?.gas_cost, totalGasCostLegacy),
    commissions: parseFinancialMetric(fmPeriod?.commissions, totalCommissionsLegacy),
    grossProfit: parseFinancialMetric(fmPeriod?.gross_profit, grossProfitLegacy),
    outstandingTickets: parseFinancialMetric(
      fmPeriod?.outstanding_tickets_value,
      parseFloat(summaryData.outstandingTickets) || 0
    ),
    growth: {
      totalEarnings: parseGrowthMetric(fmGrowth?.total_earnings),
      materialSales: parseGrowthMetric(fmGrowth?.material_sales),
      gasCost: parseGrowthMetric(fmGrowth?.gas_cost),
      commissions: parseGrowthMetric(fmGrowth?.commissions),
      grossProfit: parseGrowthMetric(fmGrowth?.gross_profit),
      outstandingTickets: parseGrowthMetric(fmGrowth?.outstanding_tickets_value),
    },
  };

  const calculatedDispatchData = extractDispatchDataFromSummary(summaryData);

  const seriesData = [
    { name: 'Hauling Sales', data: ticketCr },
    { name: 'Material Sales', data: materialCr },
    { name: 'Wait Time Charges', data: waitTimeCr },
    { name: 'Fuel Costs', data: fuelCosts },
    { name: 'Employee Commissions', data: ticketCosts },
    {
      name: 'Owner Operator Commissions',
      data: subconticketCosts.length ? subconticketCosts : [0],
    },
  ];

  const chartCacheKey = `${weekKey}_${periodKey}`;

  return {
    chartCategories: categories,
    dateRangeText: dateRange,
    calculatedFinancialData,
    calculatedDispatchData,
    seriesData,
    chartCacheKey,
  };
}

const CHART_COLORS = [
  '#008FFB', // Hauling Sales - Azul
  '#00E396', // Material Sales - Verde
  '#FEB019', // Wait Time Charges - Amarillo
  '#FF4560', // Fuel Costs - Rojo
  '#775DD0', // Employee Commissions - Púrpura
  '#3F51B5', // Owner Operator Commissions - Índigo
  '#D5EB33', // Fuel/Lube - Naranja vibrante
];

const MARKER_SIZE = 4;

/** Hide markers for 0/null so they don't sit on the X-axis under real values. */
function buildDiscreteMarkers(seriesData) {
  const discrete = [];
  seriesData.forEach((s, seriesIndex) => {
    (s.data || []).forEach((val, dataPointIndex) => {
      const n = Number(val);
      if (val == null || Number.isNaN(n) || n === 0) {
        discrete.push({
          seriesIndex,
          dataPointIndex,
          size: 0,
          fillColor: CHART_COLORS[seriesIndex % CHART_COLORS.length],
          strokeColor: '#fff',
        });
      }
    });
  });
  return discrete;
}

const CHART_HEIGHT_BY_BAND = {
  compact: 172,
  reference: 168,
  expanded: 232,
};

/** Tipo de llamada al summary del new dashboard: `full` = todos los datos (switch "New Dashboard View"). */
const CALL_TYPE_FULL = 'full';
/** Columna Dashboard / finanzas al cambiar el periodo del dropdown junto al título "Dashboard". */
const CALL_TYPE_FINANCE = 'finance';
const CALL_TYPE_TOP_DRIVERS = 'topDrivers';
/** Segmento `period` en el path para `callType=full` (resumen completo al cargar /dashboard). */
const FULL_SUMMARY_PATH_PERIOD = 'weekly';

function NewDashboardChart({
  refreshGraph,
  refreshDispatchSection,
  compacct,
  user,
  onFinancialDataUpdate,
  onDispatchDataUpdate,
  onTopDriversDataUpdate,
  onTopCustomersDataUpdate,
  layoutBand = 'reference',
  /** Periodo columna 1 (Dashboard): `currentPeriod` en el path del API. */
  chartPeriod = 'weekly',
  onChartPeriodChange,
  /** Periodo solo para Top 3 Drivers (Dispatch); el padre controla el select. */
  topDriversPeriod = 'weekly',
  /** Periodo solo para Top Customers; el padre controla el select. */
  topCustomersPeriod = 'weekly',
}) {
  const location = useLocation();
  const userId = user?.user_id;
  const compacctnum = user?.company?.compacctnum ?? compacct;

  const [series, setSeries] = useState([]);
  const [chartCategories, setChartCategories] = useState(formattedArray);
  const [dateRangeText, setDateRangeText] = useState(
    `${moment(week[0]).format('MM/DD/YYYY')} to ${moment(week[6]).format('MM/DD/YYYY')}`
  );
  const [isLoading, setIsLoading] = useState(false);
  const [financialData, setFinancialData] = useState({
    totalEarnings: 0,
    materialSales: 0,
    gasCost: 0,
    commissions: 0,
    grossProfit: 0,
    outstandingTickets: 0,
    growth: {
      totalEarnings: null,
      materialSales: null,
      gasCost: null,
      commissions: null,
      grossProfit: null,
      outstandingTickets: null,
    },
  });
  const [dispatchData, setDispatchData] = useState({
    trucksInUse: 0,
    trucksAvailable: 0,
    driversAvailable: 0,
    totalTrucks: 0,
  });
  const [topDriversData, setTopDriversData] = useState([]);
  const [topCustomersData, setTopCustomersData] = useState([]);

  const chartHeight = CHART_HEIGHT_BY_BAND[layoutBand] ?? CHART_HEIGHT_BY_BAND.reference;
  const xAxisLabelPx = layoutBand === 'reference' ? '6px' : '8px';
  const headerWrap = layoutBand !== 'reference';

  // Crear las opciones del chart dinámicamente
  const chartOptions = {
    chart: {
      type: 'area',
      height: chartHeight,
      width: '100%',
      toolbar: {
        show: false,
      },
      animations: {
        enabled: false,
      },
    },
    fill: {
      gradient: {},
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 1,
    },
    colors: CHART_COLORS,
    xaxis: {
      categories: chartCategories,
      labels: {
        rotate: -90,
        rotateAlways: true,
        style: {
          fontSize: xAxisLabelPx,
        },
        maxHeight: 35,
        offsetY: 0,
      },
    },
    yaxis: {
      decimalsInFloat: 0,
      show: false,
      forceNiceScale: false,
      min: 0,
      max: function(max) {
        return max * 1.05;
      },
    },
    grid: {
      show: true,
      padding: {
        top: 0,
        right: 5,
        bottom: 15,
        left: 5,
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
      xaxis: {
        lines: {
          show: true,
        },
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      followCursor: false,
      theme: 'light',
      style: {
        fontSize: '12px',
      },
      x: {
        show: true,
        formatter: (value, opts) => {
          const labels = opts?.w?.globals?.categoryLabels;
          const index = opts?.dataPointIndex;
          if (labels && index != null && labels[index] != null) {
            return String(labels[index]);
          }
          return value != null ? String(value) : '';
        },
      },
      y: {
        formatter: (value) => {
          if (value == null || Number.isNaN(Number(value))) return '';
          return `$${Number(value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        },
      },
    },
    legend: {
      show: false,
    },
    markers: {
      size: MARKER_SIZE,
      hover: {
        sizeOffset: 3,
      },
      discrete: buildDiscreteMarkers(series),
    },
  };

  const chartReadyKey =
    series.length > 0
      ? `ready-${chartPeriod}-${chartCategories.length}-${series[0]?.data?.length ?? 0}`
      : `empty-${chartPeriod}`;

  const fetchRunIdRef = useRef(0);
  const financeFetchGenRef = useRef(0);

  /**
   * Una sola llamada: path `.../:period/full` con `period=weekly` + periodos por widget en query.
   * Se dispara al cargar /dashboard (y al volver a la ruta) mientras exista New Dashboard View.
   */
  const loadDashboardSummary = useCallback(async () => {
    if (!userId || !compacctnum) return;

    const runId = ++fetchRunIdRef.current;
    setIsLoading(true);

    try {
      if (runId !== fetchRunIdRef.current) return;
      console.log(
        '🔄 Fetching new-dashboard-graph-summary (callType=full)',
        { pathPeriod: FULL_SUMMARY_PATH_PERIOD, chartPeriod, topDriversPeriod, topCustomersPeriod }
      );
      const summaryResponse = await client.get(
        `/jobs/new-dashboard-graph-summary/${userId}/${compacctnum}/${FULL_SUMMARY_PATH_PERIOD}/${CALL_TYPE_FULL}`,
        {
          params: {
            chartSummaryPeriod: chartPeriod,
            topDriversSummaryPeriod: topDriversPeriod,
            topCustomersSummaryPeriod: topCustomersPeriod,
          },
        }
      );
      if (runId !== fetchRunIdRef.current) return;
      const summaryData = summaryResponse.data;

      const chartPatch = buildChartFinancePatchFromSummary(summaryData, chartPeriod);
      if (chartPatch) {
        setChartCategories(chartPatch.chartCategories);
        setDateRangeText(chartPatch.dateRangeText);
        setFinancialData(chartPatch.calculatedFinancialData);
        onFinancialDataUpdate?.(chartPatch.calculatedFinancialData);
        setDispatchData(chartPatch.calculatedDispatchData);
        onDispatchDataUpdate?.(chartPatch.calculatedDispatchData);
        saveCachedChartData(userId, compacctnum, chartPatch.chartCacheKey, chartPatch.seriesData);
        setSeries(chartPatch.seriesData);
      }

      const driversList = extractTopDriversList(summaryData);
      setTopDriversData(driversList);
      onTopDriversDataUpdate?.(driversList);

      const customersList = extractTopCustomersList(summaryData);
      setTopCustomersData(customersList);
      onTopCustomersDataUpdate?.(customersList);
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
    } finally {
      if (runId === fetchRunIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    userId,
    compacctnum,
    chartPeriod,
    topDriversPeriod,
    topCustomersPeriod,
    onFinancialDataUpdate,
    onDispatchDataUpdate,
    onTopDriversDataUpdate,
    onTopCustomersDataUpdate,
  ]);

  const dispatchSectionFetchGenRef = useRef(0);

  const loadDispatchSectionOnly = useCallback(async () => {
    if (!userId || !compacctnum || !topDriversPeriod) return;

    const gen = ++dispatchSectionFetchGenRef.current;

    try {
      const summaryResponse = await client.get(
        `/jobs/new-dashboard-graph-summary/${userId}/${compacctnum}/${topDriversPeriod}/${CALL_TYPE_TOP_DRIVERS}`
      );
      if (gen !== dispatchSectionFetchGenRef.current) return;
      const summaryData = summaryResponse.data;

      const driversList = extractTopDriversList(summaryData);
      setTopDriversData(driversList);
      onTopDriversDataUpdate?.(driversList);

      const dd = extractDispatchDataFromSummary(summaryData);
      setDispatchData(dd);
      onDispatchDataUpdate?.(dd);
    } catch (error) {
      console.error('Error refreshing dispatch section:', error);
    }
  }, [userId, compacctnum, topDriversPeriod, onDispatchDataUpdate, onTopDriversDataUpdate]);

  /**
   * Al cambiar el dropdown junto a "Dashboard": path `.../:period/:callType` con callType = finance.
   */
  const fetchFinanceForPeriod = useCallback(
    async (period) => {
      if (!userId || !compacctnum || !period) return;
      const gen = ++financeFetchGenRef.current;
      setIsLoading(true);
      try {
        console.log(
          `🔄 Fetching new-dashboard-graph-summary (period=${period}, callType=${CALL_TYPE_FINANCE})…`
        );
        const res = await client.get(
          `/jobs/new-dashboard-graph-summary/${userId}/${compacctnum}/${period}/${CALL_TYPE_FINANCE}`
        );
        if (gen !== financeFetchGenRef.current) return;
        const patch = buildChartFinancePatchFromSummary(res.data, period);
        if (patch) {
          setChartCategories(patch.chartCategories);
          setDateRangeText(patch.dateRangeText);
          setFinancialData(patch.calculatedFinancialData);
          onFinancialDataUpdate?.(patch.calculatedFinancialData);
          saveCachedChartData(userId, compacctnum, patch.chartCacheKey, patch.seriesData);
          setSeries(patch.seriesData);
        }
      } catch (error) {
        console.error('Error fetching finance dashboard summary:', error);
      } finally {
        if (gen === financeFetchGenRef.current) {
          setIsLoading(false);
        }
      }
    },
    [userId, compacctnum, onFinancialDataUpdate]
  );

  const handleChartPeriodChange = useCallback(
    (event) => {
      const nextPeriod = event.target.value;
      onChartPeriodChange?.(nextPeriod);
      fetchFinanceForPeriod(nextPeriod);
    },
    [onChartPeriodChange, fetchFinanceForPeriod]
  );

  const loadDashboardSummaryRef = useRef(loadDashboardSummary);
  loadDashboardSummaryRef.current = loadDashboardSummary;

  /** Toolbar Refresh / socket: re-fetch GET .../weekly/full + periodos por widget. Al desmontar, limpia el ref para que ChartRange pueda asignar el chart clásico. */
  useEffect(() => {
    if (!refreshGraph) return;
    refreshGraph.current = () => loadDashboardSummary();
    return () => {
      if (refreshGraph) {
        refreshGraph.current = null;
      }
    };
  }, [refreshGraph, loadDashboardSummary]);

  useEffect(() => {
    if (!refreshDispatchSection) return;
    refreshDispatchSection.current = () => loadDispatchSectionOnly();
    return () => {
      if (refreshDispatchSection) {
        refreshDispatchSection.current = null;
      }
    };
  }, [refreshDispatchSection, loadDispatchSectionOnly]);

  /** Cada carga de la ruta dashboard (New Dashboard View): GET .../weekly/full + query de periodos por widget. */
  useEffect(() => {
    if (!userId || !compacctnum) return;
    const path = location.pathname.replace(/\/$/, '') || '/';
    if (path !== '/dashboard') return;
    loadDashboardSummaryRef.current?.();
  }, [userId, compacctnum, location.pathname]);

  return (
    <div className='w-full' style={{ backgroundColor: '#FFFFFF', borderRadius: '12px' }}>
      <div style={{ marginBottom: '0px' }}>
        <div
          className='flex justify-between items-center gap-2'
          style={{ marginBottom: '10px', flexWrap: headerWrap ? 'wrap' : 'nowrap' }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              color: '#000000',
              fontSize:
                layoutBand === 'compact' ? '1rem' : layoutBand === 'expanded' ? '1.35rem' : undefined,
            }}
          >
            Dashboard
          </Typography>
          <div style={{ flexShrink: 0 }}>
            <FormControl size='extra-small'
              sx={{
                '& > :first-child': {
                  paddingBottom: '3px !important',
                  paddingTop: '3px !important',
                },
              }}>
              <Select
                value={chartPeriod}
                onChange={handleChartPeriodChange}
                sx={{
                  minWidth: 120,
                  borderRadius: '50px',
                  fontSize: '1.0rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#edca33',
                    borderRadius: '50px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#edca33',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#edca33',
                  },
                  '& .MuiSelect-select': {
                    paddingBottom: '0px',
                    paddingTop: '0px',
                    paddingLeft: '0px',
                    paddingRight: '16px !important',
                  },
                }}
              >
                <MenuItem value='weekly'>Weekly</MenuItem>
                <MenuItem value='monthly'>Monthly</MenuItem>
                <MenuItem value='quarterly'>Quarterly</MenuItem>
                <MenuItem value='yearly'>Yearly</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
        <div
          className='flex justify-between items-center gap-2'
          style={{ flexWrap: headerWrap ? 'wrap' : 'nowrap' }}
        >
          <div style={{ minWidth: 0 }}>
            <Typography
              className={layoutBand === 'reference' ? 'text-18 font-Regular' : 'font-Regular'}
              sx={{
                fontWeight: 600,
                marginBottom: '0px',
                lineHeight: 1,
                ...(layoutBand === 'compact' && { fontSize: '0.95rem' }),
                ...(layoutBand === 'expanded' && { fontSize: '1.25rem' }),
              }}
            >
              ${financialData.materialSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Tooltip 
              title='Material sales for the selected period (from the dashboard summary API).'
              placement="bottom-start"
              arrow
              PopperProps={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 5],
                    },
                  },
                ],
              }}
            >
              <Typography className='text-10 font-Regular' style={{ color: '#666', cursor: 'help', marginTop: '0px' }}>
                Material Sale
              </Typography>
            </Tooltip>
          </div>
          <div style={{ textAlign: 'right', minWidth: 0, flex: headerWrap ? '1 1 140px' : undefined }}>
            <Typography
              className='font-Regular'
              style={{
                color: '#666',
                marginBottom: '4px',
                fontSize: layoutBand === 'compact' ? '9px' : layoutBand === 'expanded' ? '10px' : '8px',
              }}
            >
              Data date range:
            </Typography>
            <Typography
              className='font-Regular'
              style={{
                fontWeight: 500,
                fontSize: layoutBand === 'compact' ? '9px' : layoutBand === 'expanded' ? '10px' : '8px',
                wordBreak: 'break-word',
              }}
            >
              {dateRangeText}
            </Typography>
          </div>
        </div>
      </div>
      <div
        className='w-100-p new-dashboard-apex-chart'
        style={{
          position: 'relative',
          height: chartHeight,
          marginTop: '-20px',
          overflow: 'visible',
          zIndex: 2,
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <CircularProgress sx={{ color: '#edca33' }} />
          </Box>
        )}
        <ReactApexChart
          key={chartReadyKey}
          options={chartOptions}
          series={series}
          type='area'
          height={chartHeight}
          width='100%'
        />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.loadz_auth?.AuthReducer?.user,
});

export default memo(connect(mapStateToProps, null)(NewDashboardChart));
